
import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { ArrowLeft, ArrowRight, Grid3X3, Image, Video, ExternalLink, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PortfolioDetail from './portfolio/PortfolioDetail';

interface PortfolioItemType {
  id: number;
  title: string;
  category: string;
  client: string;
  description: string;
  media: {
    type: 'image' | 'video';
    url: string;
    thumbnail?: string;
    path?: string;
  };
}

const defaultImage = "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Back-07-05.png";

const sampleItems: PortfolioItemType[] = Array(8).fill(null).map((_, index) => {
  let specialUrl = defaultImage;
  let mediaType: 'image' | 'video' = 'image';
  let thumbnailUrl: string | undefined = undefined;

  switch (index) {

    case 0:
      return {
        id: 1,
        title: "ATS – fienden i skuggorna?",
        category: "content",
        client: "Jobb Store",
        description: "Ett kampanjinnehåll med en humoristisk twist, där vi i en sketch förklarar hur ATS-system kan stå i vägen mellan jobbsökare och arbetsgivare. Vi lyfter vanliga misstag i CV:n och erbjuder en lösning: gratis ATS-anpassning under september. Från idé och manus till regi, inspelning, redigering och casting – allt producerat av Story Store.",
        media: {
          type: "video",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Ats%20Roy%20Final-1.mp4",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//ATS.png"
        }
      };
    case 5:
      return {
        id: 6,
        title: "Spöket som sökte jobb – Halloweenkampanj",
        category: "content",
        client: "Jobb Store",
        description: "Inför Halloween skapade vi ett trendigt och underhållande content där ett spöke kämpar med att hitta jobb – tills kunden kliver in och hjälper till. Resultatet? Ett skräddarsytt spökjobb på Gröna Lund. Vi tog hand om hela produktionen: idé, manus, inspelning, skådespel, regi och redigering. En kampanj med både humor, tajming och tydligt budskap.",
        media: {
          type: "video",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//spoke.mp4",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//spoke.png"
        }
      };
    case 2:
      return {
        id: 3,
        title: "Influencerkampanj – skönhetsprodukt",
        category: "reklam",
        client: "Influencer",
        description: "En influencer behövde professionellt content till ett samarbete inom skönhet. Vi skapade visuellt tilltalande material som lyfte både produkten och influencerns varumärke. Vi stod för konceptidé, kameraarbete, ljus, regi och redigering – anpassat för att prestera på sociala medier.",
        media: {
          type: "video",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//20250328_140657000_iOS%20(1)%20(1).mp4",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Shaima.png"
        }
      };
    case 1:
      return {
        id: 2,
        title: "Ramaiana – bakom musiken",
        category: "film",
        client: "Ramaiana The Musical",
        description: "En djupgående intervju med en kompositör som berättar om sitt liv, sin kreativa resa och skapandet av Ramaiana The Musical – ett storslaget projekt baserat på den indiska mytologin, som har premiär i London 2025. Vi producerade hela filmen från idé, intervjuupplägg, inspelning och ljussättning till klippning och ljud. Ett känslostarkt berättande om passion, kultur och musik.",
        media: {
          type: "video",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Final%20Final%20Goran%20Video%201-1(1).mp4",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Goran.jpg"
        }
      };
    case 3:
      return {
        id: 4,
        title: "Behind the Pasta – äkta hantverk i köket",
        category: "content",
        client: "Buono Bistro",
        description: "En visuell upplevelse bakom kulisserna på en genuin pastarestaurang. Vi följer kökschefen när han med skicklighet och kärlek till råvarorna lagar en klassisk rätt från grunden. Story Store stod för idé, filmning och redigering – ett content-klipp som förmedlar både kvalitet och känsla.",
        media: {
          type: "video",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Bounu%20Pasta%20(1).mp4",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Buono%20Bistro.png"
        }
      };
    case 4:
      return {
        id: 5,
        title: "Visuell identitet för återvinningsföretag",
        category: "content",
        client: "Loopify",
        description: "Vi skapade en komplett visuell identitet för ett företag inom återvinning – med fokus på hållbarhet, tydlighet och framtidstro. Från färgpalett och typsnitt till logotyp och bildspråk – allt utformat för att spegla företagets vision om en grönare morgondag.",
        media: {
          type: "image",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//1744890658396_Rectangle_1_copy_1.png",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//1744890658396_Rectangle_1_copy_1.png"
        }
      };
    case 7:
      return {
        id: 8,
        title: "Modern & teaser-stil",
        category: "content",
        client: "Kommer snart",
        description: "Vi arbetar just nu med två nya produktioner inom content och visuell kommunikation. Här dyker snart upp färdigt material – producerat av Story Store från idé till leverans.",
        media: {
          type: "image",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Back-07-05.png",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Back-07-05.png"
        }
      };
    case 6:
      return {
        id: 7,
        title: "Kommande projekt",
        category: "content",
        client: "Kommer snart",
        description: "Vi jobbar på nya produktioner. Håll utkik – snart släpper vi mer content direkt från Story Store.",
        media: {
          type: "image",
          url: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Back-07-05.png",
          thumbnail: "https://mwztymqscvifvnfhxnip.supabase.co/storage/v1/object/public/portfolio-media//Back-07-05.png"
        }
      };

  }
  
  return {
    id: index + 1,
    title: `Projekt ${index + 1}`,
    category: index === 2 ? 'film' : (index % 2 === 0 ? 'content' : (index % 3 === 0 ? 'film' : 'reklam')),
    client: index === 2 ? 'Influencer' : `Kund ${index + 1}`,
    description: index === 2 
      ? 'Influencer projekt som visar kreativ videoinnehåll' 
      : 'Detta är en beskrivning av projektet. Här kan vi beskriva vad vi gjorde för kunden och vad resultatet blev.',
    media: {
      type: mediaType,
      url: specialUrl,
      thumbnail: thumbnailUrl
    }
  };
});

const PortfolioSection: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItemType[]>(sampleItems);
  const [selectedItem, setSelectedItem] = useState<PortfolioItemType | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    const element = document.getElementById('portfolio');
    if (element) observer.observe(element);
    
    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  const filteredItems = activeFilter === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === activeFilter);

  const filters = [
    { id: 'all', label: 'Alla projekt' },
    { id: 'reklam', label: 'Reklam' },
    { id: 'content', label: 'Content' },
    { id: 'film', label: 'Film' }
  ];

  const openDetail = (item: PortfolioItemType) => {
    setSelectedItem(item);
  };

  const closeDetail = () => {
    setSelectedItem(null);
  };

  const categoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'film':
        return 'bg-storystore-red text-white';
      case 'reklam':
        return 'bg-blue-500 text-white';
      case 'content':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <section id="portfolio" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Vårt <span className="text-storystore-red">portfolio</span>
          </h2>
          <p className={`text-lg text-gray-600 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Utforska våra senaste projekt och upptäck hur vi hjälper våra kunder att lyckas.
          </p>
        </div>
        
        <div className={`flex justify-center mb-8 overflow-x-auto pb-2 transition-all duration-700 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="flex space-x-2">
            {filters.map(filter => (
              <Button
                key={filter.id}
                variant={activeFilter === filter.id ? "default" : "outline"}
                onClick={() => setActiveFilter(filter.id)}
                className={`whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-storystore-red hover:bg-storystore-red/90 text-white'
                    : ''
                }`}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>
        
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 transition-all duration-700 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {filteredItems.map((item, index) => (
            <div 
              key={item.id} 
              className={`overflow-hidden rounded-lg shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 duration-300 ease-in-out transition-all delay-${(index % 8) * 100}`}
              onClick={() => openDetail(item)}
            >
              <div className="relative h-56 overflow-hidden">
                {item.media.type === 'video' ? (
                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/20">
                      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/25 backdrop-blur-sm">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-storystore-red">
                          <Video className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                    <img 
                      src={item.media.thumbnail || item.media.url} 
                      alt={item.title}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                ) : (
                  <img 
                    src={item.media.url} 
                    alt={item.title}
                    className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-500"
                  />
                )}
                <div className="absolute top-3 right-3">
                  <Badge className={`${categoryColor(item.category)}`}>
                    {item.category}
                  </Badge>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500 mb-2">{item.client}</p>
                <p className="text-sm line-clamp-2 text-gray-600">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-center mt-10">
          <Button 
            variant="outline"
            className="group flex items-center gap-2 border-storystore-red text-storystore-red hover:bg-storystore-red hover:text-white transition-colors"
          >
            Se fler projekt
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {selectedItem && (
          <PortfolioDetail 
            item={selectedItem} 
            open={!!selectedItem}
            onClose={closeDetail}
          />
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;
